import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import '@/index.css';

// Route configuration
import { publicRoutes } from '@/app/routes/routeConfig';
import { UserRole } from '@/shared/types';

// Lazy load route containers
const DashboardRoutes = lazy(() => import('@/app/routes/DashboardRoutes').catch(() => {
  return { default: () => <div>Error loading Dashboard Routes</div> };
}));

// Import admin routes directly (not lazy loaded) to fix redirect issue
import AdminRoutes from '@/app/routes/AdminRoutes';

// Email verification handler
import EmailVerificationHandler from '@/features/auth/components/EmailVerificationHandler';
// Unauthorized page
import UnauthorizedPage from '@/app/pages/UnauthorizedPage';

// Auth provider
import AuthProvider from '@/features/auth/providers/AuthProvider';

// Components
import ProtectedRoute from '@/app/components/ProtectedRoute';
import PublicRoute from '@/app/components/PublicRoute';
import AppInitializer from '@/app/components/AppInitializer';
import { PayPalProvider } from '@/features/billing/components/PayPalProvider';

import ErrorBoundary from '@/shared/components/feedback/ErrorBoundary';
import { realDataService } from '@/shared/services/realDataService';

/**
 * App - Main application component with routing
 *
 * Route Structure:
 * - Public routes: Accessible without authentication
 * - Client routes (/client/*): Protected, require client role
 * - Admin routes (/admin/*): Protected, require admin role (not lazy loaded to fix redirect issue)
 *
 * All route definitions are centralized in src/app/routes/routeConfig.ts
 */
interface RouteElement {
  element: React.LazyExoticComponent<React.ComponentType<any>>;
}

const App: React.FC = () => {
  // Helper component to render lazy loaded routes
  const renderPublicRoute = (route: RouteElement) => {
    const Element = route.element;
    return <Element />;
  };

  return (
    <ErrorBoundary>
      <AuthProvider>
        <PayPalProvider>
          <AppInitializer>
            <Router>
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info-600"></div>
            </div>
          }>
            <Routes>
              {/* ========================================================================
                  PUBLIC ROUTES
                  ======================================================================== */}

              {publicRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <PublicRoute>
                      {renderPublicRoute(route)}
                    </PublicRoute>
                  }
                />
              ))}

              {/* ========================================================================
                  EMAIL VERIFICATION
                  ======================================================================== */}

              <Route path="/verify-email" element={<EmailVerificationHandler />} />

              {/* ========================================================================
                  UNAUTHORIZED ACCESS PAGE
                  ======================================================================== */}

              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* ========================================================================
                  PROTECTED CLIENT ROUTES (/client/*)
                  ======================================================================== */}

              <Route path="/client/*" element={
                <ProtectedRoute requiredRoles={[UserRole.FREELANCER, UserRole.CLIENT]}>
                  <Suspense fallback={
                    <div className="min-h-screen flex items-center justify-center bg-gray-50">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info-600"></div>
                    </div>
                  }>
                    <DashboardRoutes onLogout={async () => {
                      await realDataService.logout();
                    }} />
                  </Suspense>
                </ProtectedRoute>
              } />

              {/* ========================================================================
                  PROTECTED ADMIN ROUTES (/admin/*)
                  ======================================================================== */}

              <Route path="/admin/*" element={
                <ProtectedRoute requiredRoles={UserRole.ADMIN}>
                  <AdminRoutes onLogout={async () => {
                    await realDataService.logout();
                  }} />
                </ProtectedRoute>
              } />

              {/* ========================================================================
                  CATCH ALL / REDIRECTS
                  ======================================================================== */}

              {/* Catch all unmatched routes - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </AppInitializer>
    </PayPalProvider>
  </AuthProvider>
</ErrorBoundary>
  );
};

export default App;
